import React, { Fragment, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import classNames from 'classnames';

import { postNativeAppCardsConfiguratorDismiss } from '../../../../services/native-bridge/native-apps-interface';
import useSaveTheDateSetup from '../../../pro/components/ContributorShare/components/ShutterbugShare/components/SaveTheDateSetup/hooks/useSaveTheDateSetup';
import AccountCardConfigurator from '../../../../services/cards/views/AccountCardConfigurator';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import useIsNativeWebView from '../../../../services/native-bridge/hooks/useIsWebView';
import { getErrorString } from '../../../../util/normalize-errors';
import useOpenAlert from '../../../../hooks/useOpenAlert';
import useRequestConfirm from '../../../../hooks/useRequestConfirm';
import Header from '../../../../components/global/Header';
import DismissButton from '../../../../components/global/DismissButton';
import useAccountCardNativeFeatureSupport from '../../../../services/cards/hooks/useAccountCardNativeFeatureSupport';
import useEdgeToEdgeViewport from '../../../../services/cards/hooks/useEdgeToEdgeViewport';
import { NATIVE_APP_LAYOUT } from '../../../../services/cards/constants';

const SaveTheDateConfigurator = () => {
  const location = useLocation();
  const openAlert = useOpenAlert();
  const requestConfirm = useRequestConfirm();
  const { albumFolderId } = useParams();
  const { reviewMode = '0' } = qs.parse(location.search);
  const [currentAccountCard, setCurrentAccountCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNativeWebView = useIsNativeWebView(true);
  const { nativeSupportsLayout } = useAccountCardNativeFeatureSupport();
  const isEdgeToEdge = nativeSupportsLayout(NATIVE_APP_LAYOUT.EDGE_TO_EDGE);

  const {
    loadingSaveTheDates,
    saveTheDateCategory,
    saveTheDateAccountCard,
    onAccountCardDelete,
    onCheckoutSuccess,
    isOwner,
    eventDate,
  } = useSaveTheDateSetup({
    albumFolderId,
  });

  useEdgeToEdgeViewport(isEdgeToEdge);

  const onConfiguratorClose = () => {
    if (!saveTheDateAccountCard && currentAccountCard) {
      requestConfirm({
        actionSize: 'xs',
        message: (
          <p className="text-center">
            <b>Are you sure you want to exit?</b>
            <br /> <br />
            Exiting now will delete the current card.
          </p>
        ),
        confirmText: 'Exit',
        cancelText: 'Cancel',
        onConfirm: async () => {
          if (saving) {
            return;
          }

          setSaving(true);
          try {
            await onAccountCardDelete(currentAccountCard);
            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
            }
            setCurrentAccountCard(null);
          } catch (e) {
            openAlert({ type: 'error', message: getErrorString(e) });
          } finally {
            setSaving(false);
          }
        },
        onCancel: () => {
          // Do nothing
        },
      });
    } else if (isNativeWebView) {
      postNativeAppCardsConfiguratorDismiss();
    }
  };

  if (!saveTheDateCategory || loadingSaveTheDates || !isOwner) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Save The Date &ndash; Waldo</title>
        <body
          className={classNames({
            'body-public-account-card': true,
            'is-native-web-view': isNativeWebView,
            'is-edge-to-edge': isEdgeToEdge,
          })}
        />
      </Helmet>
      <div className="public-account-card-wrap">
        <Header className="public-account-card-header">
          <h1 className="public-account-card-header-title">
            Send a Save The Date Card to your guests!
          </h1>
        </Header>
        {isNativeWebView && (
          <div className="public-account-card-dismiss-bar w-100 text-right px-2 mb-2">
            <DismissButton
              onClick={() => postNativeAppCardsConfiguratorDismiss()}
            />
          </div>
        )}
        <AccountCardConfigurator
          hideHeader
          onClose={onConfiguratorClose}
          title=""
          subtitle=""
          accountCardAlbumFolderId={albumFolderId}
          defaultCardCategoryId={saveTheDateCategory?.id}
          defaultDate={eventDate}
          defaultReviewingMode={reviewMode === '1'}
          onAccountCardChanged={({ accountCard }) => {
            setCurrentAccountCard(accountCard);
          }}
          onCardSelectClose={() => {
            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
            }
          }}
          onCheckoutSuccess={async () => {
            await onCheckoutSuccess();
            if (isNativeWebView) {
              postNativeAppCardsConfiguratorDismiss();
            }
          }}
          accountCardId={saveTheDateAccountCard?.id}
        />
      </div>
    </Fragment>
  );
};

export default SaveTheDateConfigurator;
